import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { STATUS_STYLES } from "../lib/api";
import { Gauge, AlarmClock, CalendarDays, TrendingUp, TrendingDown, ArrowUpRight, ArrowRight } from "lucide-react";

const chipClass = (v) => (v >= 85 ? "bg-emerald-100 text-emerald-800" : v >= 70 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700");

export default function Digest() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/digest").then((r) => setD(r.data)).catch(() => {});
  }, []);

  if (!d)
    return <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-[#14532D] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="digest-page">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Monthly Digest</h1>
        <p className="text-stone-500 mt-1 text-sm">{d.month} — {d.last_month} scores, overdue actions and this month's audits in one view.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-5" data-testid="digest-scores">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-stone-500">{d.last_month} Avg Score</p>
            <Gauge className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
          </div>
          <p className="mt-3 text-3xl font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>
            {d.scores.avg_score != null ? `${d.scores.avg_score}%` : "—"}
          </p>
          <p className="text-xs text-stone-500 mt-1">
            {d.scores.completed} audit{d.scores.completed === 1 ? "" : "s"} completed
            {d.scores.delta != null && (
              <span className={`ml-2 inline-flex items-center gap-0.5 font-semibold ${d.scores.delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {d.scores.delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {d.scores.delta > 0 ? "+" : ""}{d.scores.delta}% vs prior month
              </span>
            )}
          </p>
        </div>
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-5" data-testid="digest-overdue-kpi">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-stone-500">Overdue Actions</p>
            <AlarmClock className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
          </div>
          <p className="mt-3 text-3xl font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>{d.overdue.count}</p>
          <p className="text-xs text-stone-500 mt-1">{d.overdue.escalated} escalated to leadership</p>
        </div>
        <div className="bg-white border border-[#E7E5DF] rounded-xl p-5" data-testid="digest-upcoming-kpi">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium uppercase tracking-wider text-stone-500">Audits This Month</p>
            <CalendarDays className="w-5 h-5 text-[#14532D]" strokeWidth={1.5} />
          </div>
          <p className="mt-3 text-3xl font-extrabold tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>{d.upcoming.count}</p>
          <p className="text-xs text-stone-500 mt-1">scheduled or in progress</p>
        </div>
      </div>

      {(d.hotel_scores.length > 1 || d.weak_departments.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {d.hotel_scores.length > 1 && (
            <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="digest-hotel-scores">
              <h3 className="font-semibold text-stone-800 mb-3">{d.last_month} — Hotel Scores</h3>
              {d.hotel_scores.map((h, i) => (
                <div key={h.hotel} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0 text-sm">
                  <span className="text-stone-700 truncate pr-3"><span className="font-mono text-xs text-stone-400 mr-2">#{i + 1}</span>{h.hotel}</span>
                  <span className={`px-2 py-0.5 rounded-md font-mono text-xs font-semibold ${chipClass(h.score)}`}>{h.score}%</span>
                </div>
              ))}
            </div>
          )}
          {d.weak_departments.length > 0 && (
            <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="digest-weak-depts">
              <h3 className="font-semibold text-stone-800 mb-3">Departments Needing Attention</h3>
              {d.weak_departments.map((w) => (
                <div key={w.department} className="flex items-center justify-between py-1.5 border-b border-stone-100 last:border-0 text-sm">
                  <span className="text-stone-700">{w.department}</span>
                  <span className={`px-2 py-0.5 rounded-md font-mono text-xs font-semibold ${chipClass(w.score)}`}>{w.score}%</span>
                </div>
              ))}
              <button className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#14532D] hover:underline" onClick={() => navigate("/")}>
                Open Departmental Dashboard <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="digest-overdue-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-stone-800">Overdue Corrective Actions</h3>
          <button className="flex items-center gap-1 text-xs font-semibold text-[#14532D] hover:underline" onClick={() => navigate("/actions")}>
            View all <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {d.overdue.items.length === 0 ? (
          <p className="text-sm text-stone-400 py-6 text-center">Nothing overdue — great housekeeping.</p>
        ) : d.overdue.items.map((a, i) => (
          <div key={i} className="py-2 border-b border-stone-100 last:border-0">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-stone-800">{a.description}</p>
              {a.escalated && <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold text-white bg-rose-600 px-1.5 py-0.5 rounded uppercase"><ArrowUpRight className="w-3 h-3" />Escalated</span>}
            </div>
            <p className="text-[11px] text-stone-400 mt-0.5">{a.hotel} · {a.department} · {a.responsible} · due <span className="font-mono text-rose-600">{a.due}</span></p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl p-6" data-testid="digest-upcoming-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-stone-800">Upcoming Audits — {d.month}</h3>
          <button className="flex items-center gap-1 text-xs font-semibold text-[#14532D] hover:underline" onClick={() => navigate("/calendar")}>
            Calendar <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {d.upcoming.items.length === 0 ? (
          <p className="text-sm text-stone-400 py-6 text-center">No audits scheduled this month yet.</p>
        ) : d.upcoming.items.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-stone-100 last:border-0 cursor-pointer hover:bg-stone-50/60 rounded-lg px-1" onClick={() => navigate("/audits")}>
            <div className="min-w-0">
              <p className="text-sm text-stone-800 truncate">{a.template_name}</p>
              <p className="text-[11px] text-stone-400">{a.hotel_name} · {a.auditor_name}</p>
            </div>
            <span className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-xs text-stone-500">{a.scheduled_date}</span>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLES[a.status]}`}>{a.status.replace("_", " ")}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
